import React from "react";
import { Popover } from "antd";
import { BarChartIcon, Info } from "lucide-react";
import { Frown, Meh, SmileIcon, Smile, SmilePlus } from "lucide-react";
import moment from "moment";

const StatsPopover = ({ stats, question }) => {
  // Check if there are any stats to display
  const hasStats = () => {
    if (!question || !Array.isArray(question)) return false;

    // Check if any question has statistics
    return question.some(
      (item) =>
        item?.statsInPercentage ||
        item?.numberStatistics ||
        item?.dateStatictics,
    );
  };

  // If there are no stats at all, don't render anything
  if (!hasStats()) return null;

  // Helper function to get emoji for index

  const getEmojiForIndex = (index, scaleType = "5") => {
    // Convert index to number if it's a string
    const numIndex = parseInt(index);
    const scaleStr = scaleType.toString();

    if (scaleStr === "4") {
      switch (numIndex) {
        case 0:
          return <Frown size={18} color="#FF4D4F" className="mr-2" />;
        case 1:
          return <Meh size={18} color="#FFA940" className="mr-2" />;
        case 2:
          return <Smile size={18} color="#52C41A" className="mr-2" />;
        case 3:
          return <SmilePlus size={18} color="#1890FF" className="mr-2" />;
        default:
          return <SmileIcon size={18} color="#FADB14" className="mr-2" />;
      }
    } else if (scaleStr === "7") {
      switch (numIndex) {
        case 0:
          return <Frown size={18} color="#FF4D4F" className="mr-2" />;
        case 1:
          return <Frown size={18} color="#FF7A45" className="mr-2" />;
        case 2:
          return <Meh size={18} color="#FFA940" className="mr-2" />;
        case 3:
          return <SmileIcon size={18} color="#FADB14" className="mr-2" />;
        case 4:
          return <Smile size={18} color="#73D13D" className="mr-2" />;
        case 5:
          return <Smile size={18} color="#52C41A" className="mr-2" />;
        case 6:
          return <SmilePlus size={18} color="#1890FF" className="mr-2" />;
        default:
          return <SmileIcon size={18} color="#FADB14" className="mr-2" />;
      }
    } else {
      // Default 5-point scale
      switch (numIndex) {
        case 0:
          return <Frown size={18} color="#FF4D4F" className="mr-2" />;
        case 1:
          return <Meh size={18} color="#FFA940" className="mr-2" />;
        case 2:
          return <SmileIcon size={18} color="#FADB14" className="mr-2" />;
        case 3:
          return <Smile size={18} color="#52C41A" className="mr-2" />;
        case 4:
          return <SmilePlus size={18} color="#1890FF" className="mr-2" />;
        default:
          return <SmileIcon size={18} color="#FADB14" className="mr-2" />;
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return moment(dateString).format("MMM D, YYYY");
  };

  // Render date statistics

  const renderDateStats = (item) => {
    if (!item?.dateStatictics) return null;

    // Use the new date structure format
    const { minDate, maxDate, maxOccurrence } = item?.dateStatictics;

    return (
      <>
        <div className="mb-3 p-1.5">
          <div className="text-sm text-black font-bold mb-2">
            Date Statistics
          </div>

          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-700">Earliest </span>
            </div>
            <div className="text-xs text-gray-500">{formatDate(minDate)}</div>
          </div>

          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-700">Latest</span>
            </div>
            <div className="text-xs text-gray-500">{formatDate(maxDate)}</div>
          </div>

          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-700">Most common</span>
              <span className="text-sm text-gray-900">
                {maxOccurrence.frequency}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              {maxOccurrence.elements
                .map((date) => formatDate(date))
                .join(", ")}
            </div>
          </div>
        </div>
      </>
    );
  };

  // Render regular percentage stats
  const renderPercentageStats = (item) => {
    if (!item?.statsInPercentage && !item?.numberStatistics) return null;

    // Determine which stats to use
    const statsToRender = item.statsInPercentage || item.numberStatistics;

    if (!statsToRender) return null;

    return (
      <>
        {Object.entries(statsToRender)
          .filter(([key]) => {
            if (item?.input_type !== "slider") return true;

            const isIndex =
              !isNaN(parseInt(key, 10)) &&
              String(parseInt(key, 10)) === String(key).trim();

            if (isIndex) {
              const scaleSize =
                parseInt(item?.scale_type, 10) ||
                Object.keys(statsToRender).length;
              const idx = parseInt(key, 10);

              return idx >= 0 && idx < scaleSize;
            }

            const options = Array.isArray(item.input_options)
              ? item.input_options.map((opt) =>
                  String(opt ?? "")
                    .trim()
                    .toLowerCase(),
                )
              : [];

            if (options.length === 0) return true;

            return options.includes(String(key).trim().toLowerCase());
          })
          .map(([key, value]) => {
            const isIndex = !isNaN(parseInt(key));

            const isSlider = item && item.input_type === "slider";
            const showEmoji = isSlider && isIndex;
            const scaleType =
              item?.scale_type ||
              Object.keys(statsToRender).length.toString() ||
              "5";

            return (
              <div key={key} className="mb-3 last:mb-0 p-1.5">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-black font-bold capitalize flex items-center">
                    {showEmoji
                      ? getEmojiForIndex(key, scaleType)
                      : key.replace(/_/g, " ")}
                  </span>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-900">
                      {Math.round(value)}
                      {!item?.numberStatistics && "%"}
                    </span>
                  </div>
                </div>
                {!item?.numberStatistics && (
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-blue h-2 rounded-full transition-all duration-300"
                      style={{ width: `${value}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
      </>
    );
  };

  // Generate content for each item in the question array
  const content = (
    <div className="bg-white rounded-lg w-72 max-h-80 overflow-y-auto">
      {question?.map((item, index) => {
        // Skip items with no statistics
        if (
          !item?.statsInPercentage &&
          !item?.numberStatistics &&
          !item?.dateStatictics
        ) {
          return null;
        }

        return (
          <div key={index}>
            {item.input_title && (
              <div className="font-bold text-sm p-1.5 bg-gray-50">
                {item.input_title}
              </div>
            )}
            {}

            {item?.dateStatictics
              ? renderDateStats(item)
              : renderPercentageStats(item)}

            {index < question.length - 1 && <hr className="my-2" />}
          </div>
        );
      })}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      overlayClassName="stats-popover-overlay"
      placement="right"
    >
      <div className="inline-flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
        <span>
          <Info size={18} className="text-gray-500" />
        </span>
      </div>
    </Popover>
  );
};

export default StatsPopover;
